// NPCs editor — vendor + quest-giver capabilities, both optional.
// Master/detail shell + dirty bookkeeping is delegated to
// `createCatalogueEditor`. This file owns: blank/sanitize, the per-row
// form (identity, dialog/farewell text, vendor + quest sections), and
// the chip selectors for the two capability arrays.

import type { NpcTemplate } from "@vinyl/shared";
import {
  type CatalogueEditor,
  createCatalogueEditor,
} from "./catalogue-editor.js";
import { el } from "./dom.js";
import { numberField, textField } from "./form-fields.js";

export interface NpcsScreenCallbacks {
  getKnownItemIds: () => string[];
  getKnownQuestIds: () => string[];
  onDirtyChange: (dirty: boolean) => void;
}

export interface NpcsScreen {
  root: HTMLElement;
  setNpcs(npcs: readonly NpcTemplate[]): void;
  getDraft(): NpcTemplate[];
  isDirty(): boolean;
  markPublished(): void;
}

function blank(): NpcTemplate {
  return {
    id: "new_npc",
    name: "New NPC",
    dialogText: "Hello, traveler.",
  };
}

function sanitize(n: NpcTemplate): NpcTemplate {
  return {
    id: n.id,
    name: n.name,
    dialogText: n.dialogText,
    ...(n.farewellText?.trim() ? { farewellText: n.farewellText.trim() } : {}),
    ...(n.vendor
      ? {
          vendor: {
            stockTemplateIds: [...n.vendor.stockTemplateIds],
            sellMultiplier: n.vendor.sellMultiplier,
          },
        }
      : {}),
    ...(n.quests
      ? { quests: { questIds: [...n.quests.questIds] } }
      : {}),
  };
}

export function createNpcsScreen(cb: NpcsScreenCallbacks): NpcsScreen {
  const editor: CatalogueEditor<NpcTemplate> = createCatalogueEditor<NpcTemplate>(
    {
      entityName: "NPC",
      getName: (n) => n.name,
      blank,
      sanitize,
      showFilter: false,
      renderEditor: ({ draft, markDirty, rerender }) => renderForm(draft, markDirty, rerender),
    },
    { onDirtyChange: cb.onDirtyChange },
  );

  function renderForm(
    n: NpcTemplate,
    markDirty: () => void,
    rerender: () => void,
  ): HTMLElement {
    return el(
      "div",
      {},
      el("h2", {}, n.name || "(unnamed)"),
      section("Identity", [
        textField("id", n.id, (v) => {
          n.id = v;
          markDirty();
        }),
        textField("Name", n.name, (v) => {
          n.name = v;
          markDirty();
        }),
      ]),
      el(
        "div",
        { class: "editor-section" },
        el("h3", {}, "Dialog text"),
        textarea(n.dialogText, (v) => {
          n.dialogText = v;
          markDirty();
        }),
      ),
      el(
        "div",
        { class: "editor-section" },
        el("h3", {}, "Farewell text (optional, {name} interpolated)"),
        textarea(n.farewellText ?? "", (v) => {
          n.farewellText = v;
          markDirty();
        }),
      ),
      renderVendor(n, markDirty, rerender),
      renderQuests(n, markDirty, rerender),
      el(
        "div",
        { class: "editor-actions" },
        el(
          "button",
          {
            class: "button danger",
            onclick: () => {
              if (!confirm(`Delete "${n.name}"?`)) return;
              // Mutating the draft to a clearly-deleted shape is the
              // cleanest signal to the shell — `set()` is the only way
              // to actually remove rows, so we rebuild + re-publish.
              // Simpler: drop the row at the editor level via a
              // mutable view, then rerender.
              // The catalogue editor's `set()` rebuilds drafts; the
              // simplest "delete from draft" today is to snapshot,
              // filter, and call set() — but set() resets selection.
              // Here we accept that and re-set the whole draft.
              const next = editor.getDraft().filter((d) => d.id !== n.id);
              editor.set(next);
              // The deleted row counts as a dirty change; nudge parent.
              cb.onDirtyChange(true);
            },
          },
          "Delete from draft",
        ),
      ),
    );
  }

  function renderVendor(
    n: NpcTemplate,
    markDirty: () => void,
    rerender: () => void,
  ): HTMLElement {
    const enabled = !!n.vendor;
    const body = el("div");
    if (enabled) {
      body.append(
        el(
          "div",
          { class: "editor-grid" },
          numberField("Sell multiplier (0–1)", n.vendor!.sellMultiplier, (v) => {
            n.vendor!.sellMultiplier = Math.max(0, Math.min(1, v));
            markDirty();
          }, 0.05),
        ),
        renderChips(
          n.vendor!.stockTemplateIds,
          cb.getKnownItemIds(),
          "Add item to stock",
          (ids) => {
            n.vendor!.stockTemplateIds = ids;
            markDirty();
            rerender();
          },
        ),
      );
    }
    return el(
      "div",
      { class: "editor-section" },
      el(
        "div",
        { class: "section-toggle" },
        el(
          "label",
          {},
          (() => {
            const c = el("input", {
              type: "checkbox",
              onchange: (e: Event) => {
                if ((e.target as HTMLInputElement).checked) {
                  n.vendor = { stockTemplateIds: [], sellMultiplier: 0.25 };
                } else {
                  delete n.vendor;
                }
                markDirty();
                rerender();
              },
            });
            if (enabled) (c as HTMLInputElement).checked = true;
            return c;
          })(),
          " Vendor capability",
        ),
      ),
      body,
    );
  }

  function renderQuests(
    n: NpcTemplate,
    markDirty: () => void,
    rerender: () => void,
  ): HTMLElement {
    const enabled = !!n.quests;
    const body = el("div");
    if (enabled) {
      body.append(
        renderChips(
          n.quests!.questIds,
          cb.getKnownQuestIds(),
          "Add quest",
          (ids) => {
            n.quests!.questIds = ids;
            markDirty();
            rerender();
          },
        ),
      );
    }
    return el(
      "div",
      { class: "editor-section" },
      el(
        "div",
        { class: "section-toggle" },
        el(
          "label",
          {},
          (() => {
            const c = el("input", {
              type: "checkbox",
              onchange: (e: Event) => {
                if ((e.target as HTMLInputElement).checked) {
                  n.quests = { questIds: [] };
                } else {
                  delete n.quests;
                }
                markDirty();
                rerender();
              },
            });
            if (enabled) (c as HTMLInputElement).checked = true;
            return c;
          })(),
          " Quest-giver capability",
        ),
      ),
      body,
    );
  }

  function renderChips(
    selected: readonly string[],
    known: readonly string[],
    addLabel: string,
    onChange: (next: string[]) => void,
  ): HTMLElement {
    const wrapper = el("div", { class: "chip-list" });
    for (let i = 0; i < selected.length; i++) {
      const id = selected[i]!;
      wrapper.append(
        el(
          "span",
          { class: "chip" + (known.includes(id) ? "" : " stale") },
          id,
          el(
            "button",
            {
              type: "button",
              class: "chip-remove",
              onclick: () => onChange(selected.filter((_, j) => j !== i)),
            },
            "×",
          ),
        ),
      );
    }
    const remaining = known.filter((id) => !selected.includes(id));
    if (remaining.length > 0) {
      const sel = el(
        "select",
        {
          onchange: (e: Event) => {
            const v = (e.target as HTMLSelectElement).value;
            if (!v) return;
            onChange([...selected, v]);
            (e.target as HTMLSelectElement).value = "";
          },
        },
        el("option", { value: "" }, addLabel),
      );
      for (const id of remaining) sel.append(el("option", { value: id }, id));
      wrapper.append(sel);
    }
    return wrapper;
  }

  return {
    root: editor.root,
    setNpcs(npcs) {
      // Deep-clone the vendor / quests sub-objects so editor mutations
      // don't leak back into the published catalogue array.
      editor.set(
        npcs.map((n) => ({
          ...n,
          ...(n.vendor
            ? { vendor: { ...n.vendor, stockTemplateIds: [...n.vendor.stockTemplateIds] } }
            : {}),
          ...(n.quests ? { quests: { questIds: [...n.quests.questIds] } } : {}),
        })),
      );
    },
    getDraft: editor.getDraft,
    isDirty: editor.isDirty,
    markPublished: editor.markPublished,
  };
}

// Local helpers — kept in-file to avoid bloating form-fields.ts with
// shapes that aren't used elsewhere.

function section(title: string, children: HTMLElement[]): HTMLElement {
  return el(
    "div",
    { class: "editor-section" },
    el("h3", {}, title),
    el("div", { class: "editor-grid" }, ...children),
  );
}

function textarea(value: string, onChange: (v: string) => void): HTMLElement {
  return el(
    "textarea",
    {
      rows: 3,
      style: "width:100%;resize:vertical",
      oninput: (e: Event) => onChange((e.target as HTMLTextAreaElement).value),
    },
    value,
  );
}
